import { DropdownOption } from '@tcl-models/models';
import { TrialType } from '@tempus/trials-types';
import { DEFAULT_TABLE_PAGE_SIZE } from '~/components/_tables/BoronTableFilterable/FilterableDataTable/FilterableDataTable';
import { Reducer } from 'redux';

import { PATIENT_TRACKER_PROGRAM_OPTIONS, PATIENT_TRACKER_TAB, SORT_OPTION } from './constants';
import {
  PatientTrackerStateId,
  PatientTrackerStateMap,
  PatientTrackerActionTypes,
  SET_PATIENT_TRACKER_V3_RECORDS,
  GET_PATIENT_TRACKER_V3_RECORDS_FAILED,
  GET_PATIENT_TRACKER_V3_RECORDS,
  GET_PATIENT_TRACKER_V3_RECORDS_CSV,
  GET_PATIENT_TRACKER_V3_RECORDS_CSV_FAILED,
  SET_PATIENT_TRACKER_V3_RECORDS_CSV,
  CLEAR_PATIENT_TRACKER_V3_RECORDS_CSV,
  SET_PATIENT_TRACKER_TAB,
  SET_PATIENT_TRACKER_V3_CATEGORY_COUNTS,
  SET_PATIENT_TRACKER_V3_FILTERS,
  SET_PATIENT_TRACKER_V3_PAGINATION,
} from '../api/types/patientTrackerStore';

const PATIENT_TRACKER_TAB_DEFAULT_SORTS: Partial<Record<PATIENT_TRACKER_TAB, DropdownOption>> = {
  [PATIENT_TRACKER_TAB.VISITING_THIS_WEEK]: {
    label: SORT_OPTION.UPCOMING_VISIT_SOONEST_FIRST,
    value: 'UPCOMING_VISIT_SOONEST_FIRST',
  },
  [PATIENT_TRACKER_TAB.REVIEW_REQUIRED]: {
    label: SORT_OPTION.URGENCY,
    value: 'URGENCY',
  },
  [PATIENT_TRACKER_TAB.CANDIDATE_NOW]: {
    label: SORT_OPTION.URGENCY,
    value: 'URGENCY',
  },
  [PATIENT_TRACKER_TAB.TIME_SENSITIVE]: {
    label: SORT_OPTION.URGENCY,
    value: 'URGENCY',
  },
  [PATIENT_TRACKER_TAB.SCREENING_QUEUE]: {
    label: SORT_OPTION.URGENCY,
    value: 'URGENCY',
  },
  [PATIENT_TRACKER_TAB.PENDING_TEMPUS_REVIEW]: {
    label: SORT_OPTION.URGENCY,
    value: 'URGENCY',
  },
  [PATIENT_TRACKER_TAB.ALL]: { label: SORT_OPTION.URGENCY, value: 'URGENCY' },
  [PATIENT_TRACKER_TAB.WATCHLIST]: {
    label: SORT_OPTION.URGENCY,
    value: 'URGENCY',
  },
  [PATIENT_TRACKER_TAB.ENROLLED]: {
    label: SORT_OPTION.URGENCY,
    value: 'URGENCY',
  },
};

export const defaultFilters = {
  sortOrder: { label: SORT_OPTION.URGENCY, value: 'URGENCY' },
  trialName: null,
  trialNames: [],
  trialStatus: [],
  matchStatus: [],
  physician: '',
  indications: [],
  biomarker: '',
  patientName: '',
  note: '',
  trialType: {
    label: TrialType.INTERVENTIONAL,
    value: TrialType.INTERVENTIONAL,
  },
  patientId: '',
  rnAssignment: '',
  programs: PATIENT_TRACKER_PROGRAM_OPTIONS,
};

const defaultPatientTrackerState = {
  fetchingRecords: false,
  tab: PATIENT_TRACKER_TAB.REVIEW_REQUIRED,
  patients: [],
  patientsMetadata: {
    totalRecords: 0,
    numRecords: 15,
    skip: 0,
    top: DEFAULT_TABLE_PAGE_SIZE,
  },
  categoryCounts: {
    [PATIENT_TRACKER_TAB.REVIEW_REQUIRED]: null,
    [PATIENT_TRACKER_TAB.WATCHLIST]: null,
    [PATIENT_TRACKER_TAB.CANDIDATE_NOW]: null,
    [PATIENT_TRACKER_TAB.SCREENING_QUEUE]: null,
    [PATIENT_TRACKER_TAB.PENDING_TEMPUS_REVIEW]: null,
    [PATIENT_TRACKER_TAB.ENROLLED]: null,
    [PATIENT_TRACKER_TAB.VISITING_THIS_WEEK]: null,
    [PATIENT_TRACKER_TAB.TIME_SENSITIVE]: null,
    [PATIENT_TRACKER_TAB.ALL]: null,
  },
  filters: defaultFilters,
  exports: {
    loading: false,
    error: null,
  },
  patient: {
    loading: false,
    error: null,
  },
};

const defaultState: PatientTrackerStateMap = {
  [PatientTrackerStateId.MATCH_REVIEW_PAGE]: defaultPatientTrackerState,
  [PatientTrackerStateId.ALL_PATIENTS_PAGE]: {
    ...defaultPatientTrackerState,
    tab: PATIENT_TRACKER_TAB.WATCHLIST,
  },
  [PatientTrackerStateId.TEMPUS_NURSE_PAGE]: {
    ...defaultPatientTrackerState,
    tab: PATIENT_TRACKER_TAB.SCREENING_QUEUE,
  },
  [PatientTrackerStateId.MATERIALS_PAGE]: {
    ...defaultPatientTrackerState,
    tab: PATIENT_TRACKER_TAB.VISITING_THIS_WEEK,
  },
};

const reducer: Reducer<PatientTrackerStateMap, PatientTrackerActionTypes> = (
  state: PatientTrackerStateMap = defaultState,
  action: PatientTrackerActionTypes,
): PatientTrackerStateMap => {
  switch (action.type) {
    case SET_PATIENT_TRACKER_TAB: {
      const { id, tab } = action;

      const sortOrder: DropdownOption = PATIENT_TRACKER_TAB_DEFAULT_SORTS[tab] ?? {
        label: SORT_OPTION.URGENCY,
        value: 'URGENCY',
      };

      const updatedFilters = { ...state[id].filters, sortOrder };

      return {
        ...state,
        [id]: {
          ...state[id],
          tab,
          filters: updatedFilters,
        },
      };
    }

    case GET_PATIENT_TRACKER_V3_RECORDS: {
      const { id } = action;
      return {
        ...state,
        [id]: {
          ...state[id],
          fetchingRecords: true,
          patients: [],
        },
      };
    }

    case GET_PATIENT_TRACKER_V3_RECORDS_FAILED: {
      const { id } = action;

      return {
        ...state,
        [id]: {
          ...state[id],
          fetchingRecords: false,
        },
      };
    }

    case SET_PATIENT_TRACKER_V3_RECORDS: {
      const { id, patients } = action;
      return {
        ...state,
        [id]: {
          ...state[id],
          fetchingRecords: false,
          patients,
        },
      };
    }

    case GET_PATIENT_TRACKER_V3_RECORDS_CSV: {
      const { id } = action;
      return {
        ...state,
        [id]: {
          ...state[id],
          exports: {
            ...state[id].exports,
            loading: true,
          },
        },
      };
    }

    case GET_PATIENT_TRACKER_V3_RECORDS_CSV_FAILED: {
      const { id, error } = action;
      return {
        ...state,
        [id]: {
          ...state[id],
          exports: {
            ...state[id].exports,
            loading: false,
            error,
          },
        },
      };
    }

    case SET_PATIENT_TRACKER_V3_RECORDS_CSV: {
      const { id, data, fileName } = action;
      return {
        ...state,
        [id]: {
          ...state[id],
          exports: {
            ...state[id].exports,
            fileName,
            data,
            loading: false,
          },
        },
      };
    }

    case CLEAR_PATIENT_TRACKER_V3_RECORDS_CSV: {
      const { id } = action;
      return {
        ...state,
        [id]: {
          ...state[id],
          exports: {
            ...state[id].exports,
            data: undefined,
            fileName: undefined,
            loading: false,
          },
        },
      };
    }

    case SET_PATIENT_TRACKER_V3_CATEGORY_COUNTS: {
      const { id, categoryCounts } = action;
      return { ...state, [id]: { ...state[id], categoryCounts } };
    }

    case SET_PATIENT_TRACKER_V3_FILTERS: {
      const { id, filters } = action;
      const updatedFilters = { ...state[id].filters, ...filters };
      return { ...state, [id]: { ...state[id], filters: updatedFilters } };
    }

    case SET_PATIENT_TRACKER_V3_PAGINATION: {
      const { id, numRecords, skip, totalRecords } = action;
      const updatedPatientMetaData = { numRecords, skip, totalRecords };
      return {
        ...state,
        [id]: { ...state[id], patientsMetadata: updatedPatientMetaData },
      };
    }

    default:
      return state;
  }
};

export default reducer;
